/**
 * @contract
 *   Назначение:  Кнопка «Сообщить об ошибке в данных ЧОПа» с выпадающей формой
 *   UX:          Маленькая ссылка-кнопка → Dialog → выбор поля + описание + email
 *   Статус:      Запись в chop_error_reports со статусом 'new'
 *   Автор:       AI Agent (Фаза 46, Спринт 2)
 *   Дата:        2026-03-10
 */

"use client";

import React, { useState } from "react";
import { AlertTriangle, CheckCircle, Send } from "lucide-react";
import { Button } from "@oxpa/shared/ui/button";
import { Input } from "@oxpa/shared/ui/input";
import { Label } from "@oxpa/shared/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@oxpa/shared/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@oxpa/shared/ui/select";

interface ReportErrorButtonProps {
  companyId: string;
  companyName: string;
}

const FIELD_LABELS: Record<string, string> = {
  address: "Адрес (юридический/фактический)",
  director: "Руководитель / директор",
  name: "Название компании",
  phones: "Телефоны / контакты",
  other: "Другое",
};

export function ReportErrorButton({
  companyId,
  companyName,
}: ReportErrorButtonProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"form" | "done">("form");
  const [loading, setLoading] = useState(false);

  const [field, setField] = useState("address");
  const [comment, setComment] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim() || comment.length < 10) return;

    setLoading(true);
    try {
      await fetch("/api/seo/report-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chop_id: companyId,
          field,
          comment: comment.trim(),
          contact_email: contactEmail.trim() || null,
        }),
      });
      setStep("done");
    } catch {
      setStep("done");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setStep("form");
      setField("address");
      setComment("");
      setContactEmail("");
    }, 300);
  };

  return (
    <>
      {/* Маленькая ссылка-кнопка */}
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-[11px] text-white/30 hover:text-white/60 transition-colors uppercase tracking-widest mt-4"
      >
        <AlertTriangle className="w-3 h-3" />
        Сообщить об ошибке в данных
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-black border-white/20 text-white rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-black uppercase tracking-tight text-white flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              {step === "done" ? "Жалоба принята" : "Ошибка в данных ЧОПа"}
            </DialogTitle>
          </DialogHeader>

          {step === "done" ? (
            <div className="py-6 flex flex-col items-center justify-center gap-4 text-center">
              <div className="w-14 h-14 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-green-500" />
              </div>
              <div>
                <p className="font-bold text-white mb-1">Спасибо!</p>
                <p className="text-white/50 text-sm font-sans max-w-xs mx-auto">
                  Жалоба принята и будет рассмотрена командой модерации. Данные
                  обновятся в ближайшее время.
                </p>
              </div>
              <Button
                onClick={handleClose}
                variant="outline"
                className="border-white/20 hover:bg-white/10 text-white rounded-sm"
              >
                Закрыть
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-white/50 font-sans">
                Заметили ошибку в данных{" "}
                <strong className="text-white">{companyName}</strong>? Помогите
                нам исправить её.
              </p>

              {/* Выбор поля */}
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs uppercase tracking-widest">
                  Что исправить? *
                </Label>
                <Select value={field} onValueChange={setField}>
                  <SelectTrigger className="bg-white/5 border-white/20 text-white rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/20 text-white">
                    {Object.entries(FIELD_LABELS).map(([val, label]) => (
                      <SelectItem key={val} value={val}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Описание */}
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs uppercase tracking-widest">
                  Описание ошибки *
                  <span className="ml-2 text-white/30 normal-case">
                    (мин. 10 символов)
                  </span>
                </Label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Опишите, что именно неверно и укажите правильные данные если знаете..."
                  required
                  minLength={10}
                  maxLength={1000}
                  rows={3}
                  className="w-full bg-white/5 border border-white/20 text-white placeholder:text-white/30 rounded-sm p-3 text-sm font-sans resize-none focus:outline-none focus:ring-1 focus:ring-white/40"
                />
              </div>

              {/* Контактный email (опционально) */}
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs uppercase tracking-widest">
                  Эл. почта для обратной связи
                  <span className="ml-2 text-white/30 normal-case">
                    (необязательно)
                  </span>
                </Label>
                <Input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="ваша@почта.рф"
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/30 rounded-sm"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || comment.length < 10}
                className="w-full bg-white text-black hover:bg-white/90 rounded-sm font-black uppercase tracking-wider text-xs gap-2"
              >
                <Send className="w-4 h-4" />
                {loading ? "Отправляем..." : "Отправить жалобу"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
